import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  ReactNode,
  createElement,
} from 'react';
import {
  useCreateUgcDraftMutation,
  useUpdateUgcDraftMutation,
  useUgcDraftQuery,
} from '../../common/queries/use-ugc-draft-query';
import { UGCDraft, UGCDraftResponse } from './types';

export const MAXIMUM_TITLE_LENGTH = 125;
export const MAXIMUM_DESCRIPTION_LENGTH = 250;

type upsertUgcDraftType = {
  onSuccess: () => void;
  onError: (error: Error) => void;
};
type UgcDraftContextType = {
  ugcDraft: UGCDraft;
  updateUgcDraftFormState: (draft: Partial<UGCDraft>) => void;

  upsertUgcDraft: (props: upsertUgcDraftType) => void;

  isPersisting: boolean;
  isValid: boolean;
  hasRequiredInput: boolean;
  isTitleOverLimit: boolean;
  error: string | undefined;
  isLoading: boolean;
};

const defaultUgcDraft: UGCDraft = {
  title: '',
  body: '',
  status: 'active',
};

const UgcDraftContext = createContext<UgcDraftContextType | undefined>(
  undefined
);

type UgcDraftProviderProps = {
  children: ReactNode;
  id?: number;
};

export function UgcDraftProvider({ children, id }: UgcDraftProviderProps) {
  const isNewDraft = !id;
  const [ugcDraft, setUgcDraft] = useState<UGCDraft>(defaultUgcDraft);
  const updateUgcDraftFormState = useCallback((updates: Partial<UGCDraft>) => {
    setUgcDraft((prev) => ({ ...prev, ...updates }));
  }, []);

  const {
    data: fetchedDraft,
    isInitialLoading: isFetchingDraft,
    error: fetchError,
  } = useUgcDraftQuery(id);

  useEffect(() => {
    if (fetchedDraft && !isNewDraft) {
      setUgcDraft({
        id: fetchedDraft.data.attributes.id,
        title: fetchedDraft.data.attributes.title,
        description: fetchedDraft.data.attributes.description,
        body: fetchedDraft.data.attributes.body,
        status: fetchedDraft.data.attributes.status,
      });
    }
  }, [fetchedDraft, isNewDraft]);

  const onSaveSuccess = useCallback(
    (response: UGCDraftResponse) => {
      updateUgcDraftFormState({
        id: response.data.attributes.id,
        title: response.data.attributes.title,
        description: response.data.attributes.description,
        body: response.data.attributes.body,
        status: response.data.attributes.status,
      });
    },
    [updateUgcDraftFormState]
  );

  const onSaveError = useCallback((err) => {
    console.error('UGC draft submission error', err);
  }, []);

  const { isWorking: isCreating, mutate: createMutation } =
    useCreateUgcDraftMutation({
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });

  const { isWorking: isUpdating, mutate: updateMutateFn } =
    useUpdateUgcDraftMutation(ugcDraft.id, {
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });

  const updateMutation = useCallback(
    (
      draft: UGCDraft,
      options: { onSuccess: () => void; onError: (error: Error) => void }
    ) => {
      if (draft.id) {
        updateMutateFn(draft, options);
      }
    },
    [updateMutateFn]
  );

  const upsertUgcDraft = useCallback(
    ({ onSuccess, onError }) => {
      if (ugcDraft.id) {
        updateMutation(ugcDraft, { onSuccess, onError });
      } else {
        createMutation(ugcDraft, { onSuccess, onError });
      }
    },
    [createMutation, updateMutation, ugcDraft]
  );

  const hasRequiredInput = useMemo(
    () => !!(ugcDraft.title && ugcDraft.body),
    [ugcDraft.title, ugcDraft.body]
  );

  const isTitleOverLimit = useMemo(() => {
    if (!ugcDraft.title) {
      return false;
    }
    return ugcDraft.title.length > MAXIMUM_TITLE_LENGTH;
  }, [ugcDraft.title]);

  const isDescriptionOverLimit = useMemo(() => {
    if (!ugcDraft.description) {
      return false;
    }
    return ugcDraft.description.length > MAXIMUM_DESCRIPTION_LENGTH;
  }, [ugcDraft.description]);

  const isValid = useMemo(() => {
    const inputIsValid = !isTitleOverLimit && !isDescriptionOverLimit;
    return hasRequiredInput && inputIsValid;
  }, [isTitleOverLimit, isDescriptionOverLimit, hasRequiredInput]);

  const value = {
    ugcDraft,
    updateUgcDraftFormState,
    isPersisting: isCreating || isUpdating,
    isValid,
    hasRequiredInput,
    isTitleOverLimit,
    error: fetchError?.message,
    upsertUgcDraft,
    isLoading: isFetchingDraft,
  };

  return createElement(UgcDraftContext.Provider, { value }, children);
}

export function useUgcDraft(): UgcDraftContextType {
  const context = useContext(UgcDraftContext);
  if (context === undefined) {
    throw new Error('useUgcDraft must be used within a UgcDraftProvider');
  }
  return context;
}

export { UgcDraftContext };
